import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import {
  HighlightMenuComponent,
  HighlightMenuConfig,
} from '../views/highlight-menu/highlight-menu.component';
import { HighlightFeedbackPanelService } from './highlight-feedback-panel.service';
import { HighlightService } from './highlight.service';

@Injectable()
export class HighlightMenuService
  extends InjectableSuperclass
  implements OnDestroy
{
  private highlightMenuRef?: ComponentRef<HighlightMenuComponent>;
  private highlightMenuInstance?: HighlightMenuComponent;

  private app = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private injector = inject(Injector);

  private highlightService = inject(HighlightService);
  private highlightFeedbackPanelService = inject(HighlightFeedbackPanelService);

  constructor() {
    super();
  }

  ngOnDestroy() {
    if (this.highlightMenuRef) {
      this.app.detachView(this.highlightMenuRef.hostView);
      this.highlightMenuRef.destroy();
    }
  }

  open(option: 'add' | 'delete', clientPoint: Point2D) {
    if (isUndefined(this.highlightMenuRef)) {
      this.createFloatingMenuComponent();
    }
    assert(this.highlightMenuRef, 'Menu should be defined');

    this.highlightMenuRef.setInput('option', this.getMenuConfig(option));
    this.highlightMenuRef.setInput(
      'position',
      this.calculatePosition(clientPoint),
    );
    this.highlightMenuRef.changeDetectorRef.detectChanges();

    assert(this.highlightMenuInstance, 'Menu should be instantiated');
    this.highlightMenuInstance.open();
  }

  private getMenuConfig(type: 'add' | 'delete'): HighlightMenuConfig {
    if (type === 'add') {
      return {
        type,
        action: () => this.highlightFeedbackPanelService.showAddHighlight(),
        onCancel: () => this.highlightService.clearInProgress(),
      };
    }

    return {
      type,
      action: () => this.highlightService.deleteSelectedHighlight(),
      onClose: () => this.highlightService.clearSelected(),
    };
  }

  private calculatePosition(clientPoint: Point2D): Point2D {
    const scrollContext = document.documentElement;
    const left = clientPoint[0] + scrollContext.scrollLeft;
    const top = clientPoint[1] + scrollContext.scrollTop;
    return [left, top];
  }

  private createFloatingMenuComponent() {
    const hostElement = document.createElement(HighlightMenuComponent.selector);
    this.highlightMenuRef = createComponent(HighlightMenuComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector,
      hostElement,
    });
    this.highlightMenuInstance = this.highlightMenuRef.instance;

    this.app.attachView(this.highlightMenuRef.hostView);
    const $body = document.querySelector('body');
    assert($body, 'Body should be defined');
    $body.appendChild(this.highlightMenuRef.location.nativeElement);
  }
}
